import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Plan } from '../admin/admin';

export type CompetitionStatus = 'ACTIVE' | 'ON_HOLD';

export interface Competition {
  id: number;
  name: string;
  shortCode: string;
  startDate: string;
  endDate: string;
  submissionEndDate: string;
  maxEntries: number;
  maxAlternates: number;
  maxAge: number;
  status: CompetitionStatus;
  ownerId: number;
  /** F-009 Milestone C — true when this competition's owner is a site admin. */
  ownerIsSiteAdmin: boolean;
  /** F-021 Milestone B — gates roster-rule editability in CompetitionEditModal. */
  ownerPlan: Plan;
  /** F-015 — owner's free-text note to contestants, null until set. */
  message: string | null;
  createdAt: string;
  updatedAt: string;
}

/** F-014 — GET /competitions splits into what the caller owns vs. what
 *  they're only a contestant or cohost of (previously owner-only, which
 *  left contestant-only accounts with no way back to a competition once
 *  they navigated away from the one-time post-join redirect). */
export interface MyCompetitions {
  owned: Competition[];
  joined: Competition[];
}

export interface CreateCompetitionInput {
  name: string;
  startDate: string;
  endDate: string;
  submissionEndDate: string;
  maxEntries?: number;
  maxAlternates?: number;
  maxAge?: number;
}

/** F-009 Milestone C, Slice 7 — every field optional, omitted fields are
 *  left unchanged (matches the backend's UpdateCompetitionDto). */
export interface UpdateCompetitionInput {
  name?: string;
  startDate?: string;
  endDate?: string;
  submissionEndDate?: string;
  maxEntries?: number;
  maxAlternates?: number;
  maxAge?: number;
  message?: string | null;
}

/** Matches ContestantsService.list()'s shape on the backend. */
export interface ContestantSummary {
  id: number;
  email: string;
  displayName: string;
  pickCount: number;
  alternateCount: number;
}

/** Raw Contestant row returned by POST /competitions/join/:inviteCode — note
 *  it's keyed by competitionId, not the competition itself. */
export interface JoinResult {
  id: number;
  competitionId: number;
  userId: number;
  joinedAt: string;
}

export interface PickPerson {
  id: number;
  name: string;
  knownFor: string | null;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
  causeOfDeath: string | null;
  wikipediaUrl: string | null;
  /** Wikipedia's thumbnail image URL, if known (F-005). */
  imageUrl: string | null;
  status: 'ALIVE' | 'DEAD';
}

export interface Pick {
  id: number;
  contestantId: number;
  personId: number;
  isAlternate: boolean;
  slotOrder: number;
  promotedAt: string | null;
  createdAt: string;
  person: PickPerson;
}

export interface AddPickInput {
  personId: number;
  isAlternate?: boolean;
}

export type AwardType = 'POINTS' | 'PRIZE';
export type DistributionMode = 'DUPLICATED' | 'SPLIT';

export interface AchievementDefinition {
  id: number;
  key: string;
  name: string;
  description: string;
  isAutomatable: boolean;
}

/** A competition's configured achievement instance — `name`/`description`
 *  are only set directly for a custom achievement; for a catalog one, read
 *  them off `achievementDefinition` instead. */
export interface Achievement {
  id: number;
  competitionId: number;
  achievementDefinitionId: number | null;
  achievementDefinition: AchievementDefinition | null;
  name: string | null;
  description: string | null;
  isFixed: boolean;
  awardType: AwardType;
  pointsValue: number | null;
  prizeDescription: string | null;
  prizeDollarValue: string | null;
  distributionMode: DistributionMode;
}

/** F-021 Milestone D — manually awards a configured achievement to a
 *  contestant (competition-wide, never tied to a specific pick — the
 *  backend's AwardAchievementDto has no pickId field). splitAmount is
 *  required when the achievement's distributionMode is SPLIT, and
 *  invalid otherwise. */
export interface AwardAchievementInput {
  contestantId: number;
  splitAmount?: number;
}

/** F-021 Milestone C — either achievementDefinitionId (catalog pick) or
 *  name+description (fully custom) must be given, not both. */
export interface AddAchievementInput {
  achievementDefinitionId?: number;
  name?: string;
  description?: string;
  awardType: AwardType;
  pointsValue?: number;
  prizeDescription?: string;
  prizeDollarValue?: number;
  distributionMode?: DistributionMode;
}

/** Everything about an existing achievement that can be changed. `name` and
 *  `description` apply only to a custom achievement (a catalog one takes
 *  them from the catalog). `prizeDollarValue: null` clears the value. */
export interface UpdateAchievementInput {
  name?: string;
  description?: string;
  awardType?: AwardType;
  pointsValue?: number;
  prizeDescription?: string;
  prizeDollarValue?: number | null;
  distributionMode?: DistributionMode;
}

export interface ContestantHistoryEntry {
  competition: Competition;
  picks: Pick[];
}

export interface LeaderboardAward {
  /** F-009 Milestone E — the raw AchievementAward row id, for admin edit/delete. */
  awardId: number;
  achievementId: number;
  name: string;
  awardType: AwardType;
  points: number | null;
  prizeDescription: string | null;
  awardedAt: string;
  pickId: number | null;
  /** The picked celebrity's own details, so a leaderboard breakdown can
   *  show what each award was actually for — null alongside pickId for a
   *  competition-wide award with no specific pick behind it. */
  personName: string | null;
  dateOfDeath: string | null;
  causeOfDeath: string | null;
  distributionMode: DistributionMode;
  /** How many contestants currently hold this achievement — a SPLIT
   *  achievement is only actually split when this is more than 1. */
  winnerCount: number;
}

export interface LeaderboardEntry {
  contestantId: number;
  userId: number;
  displayName: string;
  totalPoints: number;
  awards: LeaderboardAward[];
}

/** Matches a contestant's own leaderboard awards to one specific pick.
 *  Shared between the picks page and the competition detail page's
 *  own-picks summary (F-015) — both need "which awards belong to this
 *  particular pick" from the same LeaderboardAward[]/pickId shape. */
export function awardsForPick(
  awards: LeaderboardAward[],
  pickId: number,
): LeaderboardAward[] {
  return awards.filter((a) => a.pickId === pickId);
}

/** The value of one award as shown to a contestant — "19 pts", "$25 Gift
 *  Card", and ", split 3 ways" only when the achievement really is shared
 *  (a lone winner of a SPLIT achievement gets all of it, so says nothing
 *  about splitting). A shared points award whose per-person share hasn't
 *  been set yet shows just the split note. */
export function awardValue(a: LeaderboardAward): string {
  const isShared = a.distributionMode === 'SPLIT' && a.winnerCount > 1;
  const base =
    a.awardType === 'POINTS'
      ? a.points === null && isShared
        ? null
        : `${a.points ?? 0} pts`
      : (a.prizeDescription ?? 'prize');
  return [base, isShared ? `split ${a.winnerCount} ways` : null]
    .filter((part): part is string => part !== null)
    .join(', ');
}

/** The financial/prize awards within a set of leaderboard awards, as
 *  display strings. Shared between the competition detail page's
 *  own-picks summary and the My Competitions card summary (F-017). */
export function prizeDescriptionsFor(awards: LeaderboardAward[]): string[] {
  return awards
    .filter((a) => a.awardType === 'PRIZE')
    .map((a) => a.prizeDescription ?? 'Prize');
}

/** Mirrors AuthService's pattern — HttpClient + firstValueFrom, Bearer token
 *  auto-attached by authInterceptor for any request to environment.apiBaseUrl. */
@Injectable({ providedIn: 'root' })
export class CompetitionsService {
  private readonly http = inject(HttpClient);

  create(input: CreateCompetitionInput): Promise<Competition> {
    return firstValueFrom(
      this.http.post<Competition>(
        `${environment.apiBaseUrl}/competitions`,
        input,
      ),
    );
  }

  listMine(): Promise<MyCompetitions> {
    return firstValueFrom(
      this.http.get<MyCompetitions>(`${environment.apiBaseUrl}/competitions`),
    );
  }

  getOne(id: number): Promise<Competition> {
    return firstValueFrom(
      this.http.get<Competition>(
        `${environment.apiBaseUrl}/competitions/${id}`,
      ),
    );
  }

  /** F-009 Milestone C, Slice 7 — site-admin-or-owner update via the
   *  CompetitionMembershipGuard's SITE_ADMIN bypass. */
  update(id: number, input: UpdateCompetitionInput): Promise<Competition> {
    return firstValueFrom(
      this.http.patch<Competition>(
        `${environment.apiBaseUrl}/competitions/${id}`,
        input,
      ),
    );
  }

  /** F-009 Milestone C, Slice 7 — 409s if the competition still has
   *  submitted picks (CompetitionsService.remove()'s existing guard). */
  remove(id: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.apiBaseUrl}/competitions/${id}`),
    );
  }

  listContestants(id: number): Promise<ContestantSummary[]> {
    return firstValueFrom(
      this.http.get<ContestantSummary[]>(
        `${environment.apiBaseUrl}/competitions/${id}/contestants`,
      ),
    );
  }

  /** Public, unauthenticated — used on the login page to greet someone who
   *  followed an invite link but isn't signed in yet (F-004). */
  previewInvite(inviteCode: string): Promise<{ name: string }> {
    return firstValueFrom(
      this.http.get<{ name: string }>(
        `${environment.apiBaseUrl}/competitions/invite/${inviteCode}`,
      ),
    );
  }

  join(inviteCode: string): Promise<JoinResult> {
    return firstValueFrom(
      this.http.post<JoinResult>(
        `${environment.apiBaseUrl}/competitions/join/${inviteCode}`,
        {},
      ),
    );
  }

  /** Self-service — the caller's own picks. */
  listPicks(competitionId: number): Promise<Pick[]> {
    return firstValueFrom(
      this.http.get<Pick[]>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/picks`,
      ),
    );
  }

  addPick(competitionId: number, input: AddPickInput): Promise<Pick> {
    return firstValueFrom(
      this.http.post<Pick>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/picks`,
        input,
      ),
    );
  }

  removePick(competitionId: number, pickId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/picks/${pickId}`,
      ),
    );
  }

  /** F-009 Milestone D, Slice 9 — site-admin-only, bypasses the invite-code
   *  flow. Identifies the target by email. Caller reloads the contestants
   *  list afterward rather than consuming the response body.
   *
   *  F-028 — displayName is only used when `email` doesn't match an
   *  existing account: the backend then creates a throwaway, unverified
   *  contestant under that name for recreating historical entries.
   *  Ignored server-side when the user already exists. */
  addContestant(
    competitionId: number,
    email: string,
    displayName?: string,
  ): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/contestants`,
        { email, ...(displayName ? { displayName } : {}) },
      ),
    );
  }

  /** Owner, cohost, or site admin (F-009 Milestone D, Slice 8). */
  kick(competitionId: number, userId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/contestants/${userId}`,
      ),
    );
  }

  /** Cohost-view of a specific contestant's picks for this competition. */
  listPicksForContestant(competitionId: number, userId: number): Promise<Pick[]> {
    return firstValueFrom(
      this.http.get<Pick[]>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/contestants/${userId}/picks`,
      ),
    );
  }

  /** Owner-or-cohost route; a site admin adding a DEAD person's pick
   *  (F-028, recreating historical entries) is transparently allowed
   *  server-side when the caller is actually SITE_ADMIN — nothing extra
   *  to pass here for that. */
  addPickForContestant(
    competitionId: number,
    userId: number,
    input: AddPickInput,
  ): Promise<Pick> {
    return firstValueFrom(
      this.http.post<Pick>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/contestants/${userId}/picks`,
        input,
      ),
    );
  }

  removePickForContestant(
    competitionId: number,
    userId: number,
    pickId: number,
  ): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/contestants/${userId}/picks/${pickId}`,
      ),
    );
  }

  listAchievements(competitionId: number): Promise<Achievement[]> {
    return firstValueFrom(
      this.http.get<Achievement[]>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/achievements`,
      ),
    );
  }

  /** F-021 Milestone C — the global achievement catalog (11 seeded rows),
   *  open to any authenticated user so a paid-tier owner can browse it. */
  listAchievementDefinitions(): Promise<AchievementDefinition[]> {
    return firstValueFrom(
      this.http.get<AchievementDefinition[]>(
        `${environment.apiBaseUrl}/achievement-definitions`,
      ),
    );
  }

  /** F-021 Milestone C — owner-only, paid-tier-only (backend 403s a FREE
   *  owner), and capped at the plan's maxAchievements (backend 409s once
   *  full). */
  addAchievement(
    competitionId: number,
    input: AddAchievementInput,
  ): Promise<Achievement> {
    return firstValueFrom(
      this.http.post<Achievement>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/achievements`,
        input,
      ),
    );
  }

  /** Owner only, paid tier only. Not allowed for Age (computed). */
  updateAchievement(
    competitionId: number,
    achievementId: number,
    input: UpdateAchievementInput,
  ): Promise<Achievement> {
    return firstValueFrom(
      this.http.patch<Achievement>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/achievements/${achievementId}`,
        input,
      ),
    );
  }

  /** F-021 Milestone D — owner-or-cohost (backend enforces both), for a
   *  manually-awardable achievement only (custom, or non-automatable, or
   *  SPLIT distribution — a fully automated DUPLICATED catalog
   *  achievement 403s, that's the scoring engine's job). Idempotent per
   *  (achievement, contestant): calling it again for the same contestant
   *  updates their splitAmount rather than erroring. */
  awardAchievement(
    competitionId: number,
    achievementId: number,
    input: AwardAchievementInput,
  ): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/achievements/${achievementId}/awards`,
        input,
      ),
    );
  }

  /** F-009 Milestone E, Slice 10 — owner or site admin; 409 for a fixed
   *  achievement (Age/First to Die/Last Gasp/Grim Reaper), blocked for everyone. */
  removeAchievement(competitionId: number, achievementId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/achievements/${achievementId}`,
      ),
    );
  }

  /** F-009 Milestone E, Slice 11 — site-admin-only raw correction. */
  updateAchievementAward(
    competitionId: number,
    achievementId: number,
    awardId: number,
    input: { points?: number; splitAmount?: number },
  ): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/achievements/${achievementId}/awards/${awardId}`,
        input,
      ),
    );
  }

  /** F-009 Milestone E, Slice 11 — site-admin-only. */
  removeAchievementAward(
    competitionId: number,
    achievementId: number,
    awardId: number,
  ): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/achievements/${achievementId}/awards/${awardId}`,
      ),
    );
  }

  /** Every competition this competition's owner runs where userId is/was a
   *  contestant, each with their picks there. */
  getContestantHistory(
    competitionId: number,
    userId: number,
  ): Promise<ContestantHistoryEntry[]> {
    return firstValueFrom(
      this.http.get<ContestantHistoryEntry[]>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/contestants/${userId}/history`,
      ),
    );
  }

  getLeaderboard(competitionId: number): Promise<LeaderboardEntry[]> {
    return firstValueFrom(
      this.http.get<LeaderboardEntry[]>(
        `${environment.apiBaseUrl}/competitions/${competitionId}/leaderboard`,
      ),
    );
  }
}
